import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Space, Card, Tag, Input, Row, Col, Statistic, App, Tooltip } from 'antd';
import { PlusOutlined, SearchOutlined, TeamOutlined, MailOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { trpc } from '../../../lib/api';
import { adminPath } from '../../../lib/adminPaths';
import { CreateAgencyModal } from './CreateAgencyModal';
import { ResponsiveTable, type ResponsiveColumnType } from '../../../components/common/responsive';

const { Search } = Input;

interface AgencyUser {
  id: string;
  email: string;
  name: string;
  companyName: string | null;
  couponPrefix: string;
  phone: string;
  agencyTier: number;
  commissionRate: string;
  isActive: boolean;
  emailVerified: boolean;
  createdAt: Date | string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const normalizeAgencyUser = (value: unknown): AgencyUser | null => {
  if (!isRecord(value) || typeof value.id !== 'string') {
    return null;
  }

  return {
    id: value.id,
    email: typeof value.email === 'string' ? value.email : '',
    name: typeof value.name === 'string' ? value.name : '',
    companyName: typeof value.companyName === 'string' ? value.companyName : null,
    couponPrefix: typeof value.couponPrefix === 'string' ? value.couponPrefix : '',
    phone: typeof value.phone === 'string' ? value.phone : '',
    agencyTier: typeof value.agencyTier === 'number' ? value.agencyTier : 0,
    commissionRate: typeof value.commissionRate === 'string' ? value.commissionRate : '0',
    isActive: typeof value.isActive === 'boolean' ? value.isActive : false,
    emailVerified: typeof value.emailVerified === 'boolean' ? value.emailVerified : false,
    createdAt: typeof value.createdAt === 'string' ? value.createdAt : '',
  };
};

export function AgencyListPage() {
  const { message } = App.useApp();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  // 代理店一覧取得
  const { data, isLoading } = trpc.agencyAdmin.listAgencyUsers.useQuery({
    searchQuery: searchQuery || undefined,
    page: currentPage,
    limit: pageSize,
  });

  // 代理店停止/再開
  const suspendMutation = trpc.agencyAdmin.suspendAgencyUser.useMutation({
    onSuccess: (_, variables) => {
      const input = isRecord(variables) ? variables : null;
      const suspend = typeof input?.suspend === 'boolean' ? input.suspend : false;
      message.success(suspend ? '代理店を停止しました' : '代理店を再開しました');
      utils.agencyAdmin.listAgencyUsers.invalidate();
    },
    onError: (error) => {
      message.error(error.message || '操作に失敗しました');
    },
  });

  // 招待メール再送
  const resendInvitationMutation = trpc.agencyInvitation.resend.useMutation({
    onSuccess: () => {
      message.success('招待メールを再送しました');
    },
    onError: (error) => {
      message.error(error.message || '招待メールの再送に失敗しました');
    },
  });

  const users = useMemo(
    () => (data?.users ?? []).flatMap((user) => {
      const normalized = normalizeAgencyUser(user);
      return normalized ? [normalized] : [];
    }),
    [data?.users]
  );

  const columns: ResponsiveColumnType<AgencyUser>[] = [
    {
      title: 'プレフィックス',
      dataIndex: 'couponPrefix',
      key: 'couponPrefix',
      width: 120,
      render: (prefix) => <code style={{ fontWeight: 'bold' }}>{prefix}</code>,
      mobileRole: 'tag',
      mobilePriority: 2,
    },
    {
      title: '代理店名',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <a onClick={() => navigate(adminPath(`agencies/${record.id}`))} style={{ fontWeight: 'bold' }}>
            {text}
          </a>
          {record.companyName && (
            <div style={{ fontSize: '12px', color: '#999' }}>{record.companyName}</div>
          )}
        </div>
      ),
      mobileRole: 'title',
      mobilePriority: 1,
    },
    {
      title: '連絡先',
      key: 'contact',
      render: (_, record) => (
        <div style={{ fontSize: '12px' }}>
          <div>{record.email}</div>
          <div>{record.phone}</div>
        </div>
      ),
      mobileRole: 'description',
      mobilePriority: 3,
    },
    {
      title: '階層',
      dataIndex: 'agencyTier',
      key: 'agencyTier',
      width: 80,
      align: 'center' as const,
      mobileRole: 'hidden',
    },
    {
      title: '手数料レート',
      dataIndex: 'commissionRate',
      key: 'commissionRate',
      width: 120,
      align: 'right' as const,
      render: (rate) => `${parseFloat(rate)}%`,
      mobileRole: 'description',
      mobilePriority: 4,
    },
    {
      title: 'ステータス',
      key: 'status',
      width: 140,
      render: (_, record) => (
        <Space direction="vertical" size="small">
          {record.isActive ? <Tag color="green">有効</Tag> : <Tag color="red">停止</Tag>}
          {record.emailVerified ? (
            <Tag icon={<CheckCircleOutlined />} color="success">認証済み</Tag>
          ) : (
            <Tag icon={<CloseCircleOutlined />} color="warning">未認証</Tag>
          )}
        </Space>
      ),
      mobileRole: 'tag',
      mobilePriority: 5,
    },
    {
      title: '登録日',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date) => new Date(date).toLocaleDateString('ja-JP'),
      mobileRole: 'hidden',
    },
    {
      title: '操作',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space size="small" direction="vertical">
          <Space size="small">
            <Button
              type="link"
              size="small"
              onClick={() => navigate(adminPath(`agencies/${record.id}`))}
            >
              詳細
            </Button>
            <Button
              type="link"
              size="small"
              onClick={() =>
                suspendMutation.mutate({
                  agencyUserId: record.id,
                  suspend: !record.isActive,
                  suspendedReason: record.isActive ? '管理者による停止' : undefined,
                })
              }
            >
              {record.isActive ? '停止' : '再開'}
            </Button>
          </Space>
          {!record.emailVerified && (
            <Tooltip title="初回パスワード設定用の招待メールを再送します">
              <Button
                type="link"
                size="small"
                icon={<MailOutlined />}
                loading={resendInvitationMutation.isPending}
                onClick={() =>
                  resendInvitationMutation.mutate({
                    agencyUserId: record.id,
                  })
                }
              >
                招待メール再送
              </Button>
            </Tooltip>
          )}
        </Space>
      ),
      mobileRole: 'hidden',
    },
  ];

  return (
    <div>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 style={{ margin: 0 }}>代理店管理</h1>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsCreateModalOpen(true)}
          >
            新規代理店作成
          </Button>
        </div>

        <Row gutter={16}>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="総代理店数"
                value={data?.pagination.total || 0}
                prefix={<TeamOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="有効な代理店"
                value={users.filter((user) => user.isActive).length || 0}
                prefix={<TeamOutlined />}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
        </Row>

        <Card>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Row gutter={16}>
              <Col flex="auto">
                <Search
                  placeholder="代理店名、会社名、メールアドレス、プレフィックスで検索"
                  allowClear
                  enterButton={<SearchOutlined />}
                  onSearch={setSearchQuery}
                  style={{ width: '100%' }}
                />
              </Col>
            </Row>

            <ResponsiveTable
              dataSource={users}
              columns={columns}
              loading={isLoading}
              rowKey="id"
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: data?.pagination.total || 0,
                onChange: (page, size) => {
                  setCurrentPage(page);
                  setPageSize(size || 20);
                },
                showSizeChanger: true,
                showTotal: (total) => `全 ${total} 件`,
              }}
              mobileCardConfig={{
                titleField: 'name',
                onClick: (record) => navigate(adminPath(`agencies/${record.id}`)),
                actions: (record) => (
                  <Space>
                    <Button
                      type="link"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        suspendMutation.mutate({
                          agencyUserId: record.id,
                          suspend: !record.isActive,
                          suspendedReason: record.isActive ? '管理者による停止' : undefined,
                        });
                      }}
                    >
                      {record.isActive ? '停止' : '再開'}
                    </Button>
                  </Space>
                ),
              }}
            />
          </Space>
        </Card>
      </Space>

      <CreateAgencyModal
        open={isCreateModalOpen}
        onCancel={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          setIsCreateModalOpen(false);
          utils.agencyAdmin.listAgencyUsers.invalidate();
        }}
      />
    </div>
  );
}
